import cookies from "js-cookie";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

/**
 * Name of CSRF Token Header
 */
export const CSRF_HEADER = "X-CSRFToken";

/**
 * Get the current locale from the document's lang attribute.
 * Django sets this based on the active locale, so it's the authoritative source.
 * Falls back to "en" if not available (e.g., in SSR context).
 */
export const getCurrentLocale = (): string => {
    const document = getDocument();
    if (!document) {
        return "en";
    }
    return document.documentElement.lang || "en";
};

/**
 * Load a CSRF token using the API. Sometimes necessary since CSRF tokens aren't included in cached page responses.
 */
export const loadCSRFToken = async (): Promise<string> => {
    const resp = await wafAwareFetch("/api/csrf-token/", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
    });
    const data = (await resp.json()) as { csrf_token: string };
    return data.csrf_token;
};

let loadingCSRFToken: Promise<string> | null = null;

/**
 * Get the value of the CSRF Token Cookie, loading a token when there is none.
 *
 * Concurrent callers share one load: each load mints a new cookie, so tokens
 * from parallel loads would not all match the cookie the browser ends up with.
 */
export const getCSRFToken = async (): Promise<string> => {
    const token = cookies.get("csrftoken");
    if (token) {
        return token;
    }
    loadingCSRFToken ??= loadCSRFToken().finally(() => {
        loadingCSRFToken = null;
    });
    return loadingCSRFToken;
};

/**
 * Load a CSRF token in idle time for a visitor who has none.
 *
 * Reads no longer start a session, so nothing sets the CSRF cookie before a
 * visitor's first write. Call this where that first write is likely (add to
 * cart) so it doesn't wait on an extra round trip for the token.
 */
export const prefetchCSRFToken = (): void => {
    const load = () => {
        getCSRFToken().catch(() => undefined);
    };
    if ("requestIdleCallback" in globalThis) {
        requestIdleCallback(load);
    } else {
        setTimeout(load, 1);
    }
};

/**
 * Client-side API exposed by the AWS WAF JavaScript challenge SDK, loaded on
 * every page by each site's PageSkeleton (chrome.aws_waf_url).
 */
interface AwsWafIntegration {
    fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

/**
 * True when the URL targets the current origin (relative URLs included).
 */
const isSameOrigin = (url: string): boolean => {
    const window = getWindow();
    if (!window) {
        return false;
    }
    try {
        return (
            new URL(url, window.location.href).origin === window.location.origin
        );
    } catch {
        return false;
    }
};

/**
 * Fetch through the AWS WAF SDK when it's loaded and the request targets our
 * own origin. The SDK's fetch wrapper acquires a WAF token and attaches it to
 * the request, preventing AWS WAF from answering API calls with its HTML
 * challenge interstitial (served as HTTP 202). Cross-origin requests stay on
 * plain fetch: the token header isn't CORS-safelisted (it would force
 * preflights) and would leak our WAF token to third parties.
 */
const wafAwareFetch = (url: string, init: RequestInit): Promise<Response> => {
    const waf = (globalThis as { AwsWafIntegration?: AwsWafIntegration })
        .AwsWafIntegration;
    return waf && isSameOrigin(url) ? waf.fetch(url, init) : fetch(url, init);
};

/**
 * Custom error class for HTTP errors with response body access
 */
export class HttpError extends Error {
    readonly status: number;
    readonly body: unknown;
    readonly response: { readonly body: unknown; readonly status: number };

    constructor(message: string, status: number, body: unknown) {
        super(message);
        this.name = "HttpError";
        this.status = status;
        this.body = body;
        // For backward compatibility with superagent error pattern
        this.response = { body, status };
    }
}

const SAFE_METHODS = ["GET", "HEAD", "OPTIONS", "TRACE"];

/** How long a held write waits on the one sent before it, then sends anyway. */
const WRITE_HOLD_MS = 10_000;

/**
 * Until a write to our own origin gets an answer, the visitor may have no
 * session: the write that starts one logs them in as a new guest and rotates
 * the CSRF token. Until then writes go one at a time, so none carries a stale
 * token or makes a second guest.
 */
let identityEstablished = false;
let writeTurn: Promise<void> = Promise.resolve();

const isCSRFFailure = (status: number, body: unknown): boolean =>
    status === 403 &&
    typeof body === "object" &&
    body !== null &&
    "detail" in body &&
    typeof body.detail === "string" &&
    body.detail.startsWith("CSRF Failed");

/**
 * Helper to parse JSON response and throw HttpError on non-OK status.
 *
 * Until a write to our own origin has had an answer, such writes are sent one
 * at a time, each with the CSRF token current when it goes. A write still
 * refused for its CSRF token is sent once more if the token has changed; the
 * refusal comes before the view runs, so the resend cannot apply it twice.
 */
export const fetchJson = async <T>(
    url: string,
    options?: RequestInit,
    resendOnNewToken = true,
): Promise<T> => {
    const headers = new Headers(options?.headers);
    if (!headers.has("Accept")) {
        headers.set("Accept", "application/json");
    }
    if (!headers.has("Accept-Language")) {
        headers.set("Accept-Language", getCurrentLocale());
    }
    const isWrite =
        !SAFE_METHODS.includes(options?.method?.toUpperCase() ?? "GET") &&
        isSameOrigin(url);
    const send = () =>
        wafAwareFetch(url, {
            ...options,
            credentials: options?.credentials ?? "same-origin",
            headers,
        });
    let resp: Response;
    if (isWrite && !identityEstablished) {
        const turn = writeTurn;
        let releaseTurn: () => void = () => {};
        writeTurn = new Promise<void>((resolve) => {
            releaseTurn = () => resolve();
        });
        const sending = turn.then(async () => {
            if (headers.has(CSRF_HEADER)) {
                headers.set(CSRF_HEADER, await getCSRFToken());
            }
            const transport = send();
            setTimeout(releaseTurn, WRITE_HOLD_MS);
            return transport;
        });
        sending.then(releaseTurn, releaseTurn);
        resp = await sending;
        // Neither a WAF challenge nor a 5xx (Django saves no session on one)
        // leaves the visitor signed in.
        if (resp.status < 500 && !resp.headers.has("x-amzn-waf-action")) {
            identityEstablished = true;
        }
    } else {
        resp = await send();
    }
    const sentToken = headers.get(CSRF_HEADER);

    // Handle 204 No Content responses (e.g. successful DELETE operations)
    if (resp.status === 204) {
        return undefined as T;
    }

    const contentType = resp.headers.get("content-type");
    const body = contentType?.includes("application/json")
        ? await resp.json()
        : await resp.text();

    if (!resp.ok) {
        if (
            resendOnNewToken &&
            sentToken !== null &&
            isCSRFFailure(resp.status, body)
        ) {
            const token = await getCSRFToken();
            if (token !== sentToken) {
                headers.set(CSRF_HEADER, token);
                return fetchJson<T>(url, { ...options, headers }, false);
            }
        }
        throw new HttpError(
            `HTTP ${resp.status}: ${resp.statusText}`,
            resp.status,
            body,
        );
    }

    // An HTML body on one of our own JSON endpoints means an intermediary
    // intercepted the request — most commonly AWS WAF's challenge
    // interstitial, which is served with a 2xx status (202). Surface it as an
    // HttpError (without embedding the HTML in the message) instead of
    // handing the HTML to callers' io-ts decoders. Cross-origin endpoints
    // keep the legacy text pass-through behavior.
    if (contentType?.includes("text/html") && isSameOrigin(url)) {
        throw new HttpError(
            `Expected JSON response but got ${contentType} (HTTP ${resp.status})`,
            resp.status,
            body,
        );
    }

    return body as T;
};
